import { Chip, createStyles, Grid, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CustomPalette, MaterialPalette } from '@nutrien/cxp-components';
import { Auth } from 'aws-amplify';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { sentryCaptureException, syncHelper, useOnlineStatus } from '../../utilities';
import DiscardDraftModal from '../DiscardDraftModal';
import { triggerPWAUpdate } from '../PWAUpdate/PWAUpdate';
import { logError } from '../Shared/analytics';
import SignatureAndStamp from '../UserSignatureAndStamp/SignatureAndStamp';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      '& .button': {
        width: 'fit-content',
        '& .chip': {
          marginLeft: '8px',
          backgroundColor: `${MaterialPalette.error.main}`,
        },
      },
    },
    container: { maxWidth: '340px' },
    paper: {
      maxWidth: '300px',
      minWidth: '250px',
      padding: '8px',
      backgroundColor: CustomPalette.elevation.dp24Solid,
    },
  }),
);

interface Props {
  id: string;
  open: boolean;
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
  onLogout: () => void;
}

const UserMenu: React.FC<Props> = ({ id, open, anchorEl, onClose, onLogout }: Props) => {
  const classes = useStyles();
  const { appVersion } = useMst();
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');
  const [openDialogAnchorEI, setOpenDialogAnchorEI] = useState<HTMLButtonElement | null>(null);
  const isOnline = useOnlineStatus();
  const { saveDeferUpdateTimeToLocalStorage } = syncHelper();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);

  const onInstallUpdate = async () => {
    try {
      await triggerPWAUpdate();
    } catch (error) {
      logError(error, false, '~ file: UserMenu.tsx ~ line 51 ~ onInstallUpdate ~');
      console.error('🚀 ~ file: UserMenu.tsx ~ line 50 ~ onInstallUpdate ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: UserMenu.tsx ~ line 51 ~ onInstallUpdate ~');
    }
  };

  const onDeferUpdate = () => {
    saveDeferUpdateTimeToLocalStorage();
    setUpdateModalOpen(false);
    onClose();
  };

  const onLogoutClicked = () => {
    onClose();
    onLogout();
  };

  const getUserName = async () => {
    return Auth.currentAuthenticatedUser({ bypassCache: true });
  };

  const getLoggedIUser = async () => {
    try {
      const loggedInuser = await getUserName();
      const user = await loggedInuser.getSignInUserSession();
      setLoggedInUserName(user.idToken.payload.Email);
    } catch (error) {
      console.log('🚀 ~ file: UserMenu.ts ~ line 75 ~ getLoggedIUser ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: UserMenu.tsx ~ line 75 ~ getLoggedIUser ~');
    }
  };

  const openSignatureAndStampDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenDialogAnchorEI(event.currentTarget);
  };

  const handleClose = () => {
    setOpenDialogAnchorEI(null);
  };

  const onRefreshClicked = () => {
    window.location.reload();
  };

  useEffect(() => {
    getLoggedIUser();
  }, []);

  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{ paper: classes.paper }}
      >
        <Grid container spacing={1} className={classes.root}>
          <Grid item xs={12}>
            <Button variant="text" color="white" noMinHeight className="button">
              {loggedInUserName}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <div>
              <Button
                variant="text"
                color="white"
                noMinHeight
                className="button"
                onClick={e => openSignatureAndStampDialog(e)}
                data-testid="signAndStampButton"
              >
                Signature & Stamp
              </Button>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div>
              <Button
                variant="text"
                color="white"
                noMinHeight
                onClick={() => setUpdateModalOpen(true)}
                disabled
                className="button"
              >
                {`Version ${appVersion.currentAppVersion}`}
                {appVersion.hasNewUpdate && (
                  <Chip label="Update Available" size="small" className="chip" />
                )}
              </Button>
            </div>
          </Grid>
          <Grid item container xs={12}>
            <Button
              variant="text"
              color="white"
              noMinHeight
              onClick={onRefreshClicked}
              className="button"
              data-testid="refreshButton"
            >
              Refresh Page
            </Button>
          </Grid>

          <Grid item container xs={12}>
            <Button
              variant="text"
              color="white"
              noMinHeight
              onClick={onLogoutClicked}
              className="button"
              disabled={!isOnline}
              data-testid="logoutOption"
            >
              Log Out
            </Button>
          </Grid>
        </Grid>
      </Popover>
      <DiscardDraftModal
        open={updateModalOpen}
        onCancel={onDeferUpdate}
        onDiscard={onInstallUpdate}
        disableActionButton={!isOnline}
        flipColors
        titleText="Software update"
        areYouSureText="Software update required."
        cancelText={
          isOnline
            ? 'Would you like to download and install this update now?'
            : 'You must be online to install the update, please connect to WiFi or move to an area with better connectivity.'
        }
        continueEditingButtonText="Defer Update"
        discardDraftButtonText="Install"
        disableEnforceFocus
      />
      <SignatureAndStamp
        id="user-menu"
        open={Boolean(openDialogAnchorEI)}
        anchorEl={openDialogAnchorEI}
        onClose={handleClose}
      />
    </>
  );
};

export default observer(UserMenu);
